import React, { useState } from 'react';
import { Check, Shield, Star, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { UrgencyBanner } from '../UrgencyBanner';
interface OfferBlockProps {
  heading: string;
}
interface Plan {
  id: 'pro' | 'total';
  name: string;
  desc: string;
  oldPrice: string;
  price: string;
  href: string;
  mostChosen?: boolean;
}
const PLANS: Plan[] = [{
  id: 'pro',
  name: 'MEGAPACK PRO',
  desc: '6 tomos + 3 bonos de consulta rápida',
  oldPrice: '$39.990',
  price: '$24.990',
  href: 'https://checkout.solucionesya.shop/cart/49919274647800:1'
}, {
  id: 'total',
  name: 'EDICIÓN TOTAL',
  desc: '6 tomos + 3 bonos + 2 tomos premium (Legal + Interdisciplinario)',
  oldPrice: '$49.990',
  price: '$34.990',
  href: 'https://checkout.solucionesya.shop/cart/49919275041016:1',
  mostChosen: true
}];
export function OfferBlock({
  heading
}: OfferBlockProps) {
  // Higher-priced Edición Total is preselected by default
  const [selectedId, setSelectedId] = useState<Plan['id']>('total');
  const selected = PLANS.find(p => p.id === selectedId)!;
  return <div data-id="element-77">
      {/* Urgency module — appears above the offer */}
      <UrgencyBanner data-id="element-78" />

      {/* Offer Header */}
      <div className="text-center mb-6 md:mb-10" data-id="element-79">
        <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900" data-id="element-80">
          {heading}
        </h2>
      </div>

      {/* Selectable Options — compact horizontal rows, both visible without scrolling */}
      <div role="radiogroup" aria-label="Elegí tu opción" className="flex flex-col gap-3 max-w-2xl mx-auto mb-5 md:mb-6" data-id="element-81">
        {PLANS.map(plan => {
        const isSelected = plan.id === selectedId;
        const darkSelected = isSelected && plan.mostChosen;
        return <button key={plan.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelectedId(plan.id)} className={`relative w-full text-left rounded-2xl px-4 py-3 md:px-5 md:py-4 border-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isSelected ? plan.mostChosen ? 'border-amber-400 bg-blue-900 shadow-lg shadow-blue-900/20' : 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-82">
              <div className="flex items-center gap-3 md:gap-4" data-id="element-83">
                {/* Radio indicator */}
                <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? plan.mostChosen ? 'border-amber-400 bg-amber-400' : 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`} data-id="element-84">
                  {isSelected && <Check className={`w-3 h-3 ${plan.mostChosen ? 'text-blue-900' : 'text-white'}`} strokeWidth={3} data-id="element-85" />}
                </span>

                {/* Name + desc */}
                <div className="min-w-0 flex-1" data-id="element-86">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-87">
                    <h3 className={`font-bold text-sm md:text-lg leading-tight ${darkSelected ? 'text-white' : 'text-slate-900'}`} data-id="element-88">
                      {plan.name}
                    </h3>
                    {plan.mostChosen && <span className="bg-amber-400 text-amber-950 text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-sm" data-id="element-89">
                        <Star className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-90" /> Más
                        elegida
                      </span>}
                  </div>
                  <p className={`text-[11px] md:text-sm mt-0.5 leading-snug ${darkSelected ? 'text-blue-200' : 'text-slate-500'}`} data-id="element-91">
                    {plan.desc}
                  </p>
                </div>

                {/* Prices — right aligned */}
                <div className="flex-shrink-0 text-right leading-none" data-id="element-92">
                  <div className={`text-xs line-through ${darkSelected ? 'text-blue-400' : 'text-slate-400'}`} data-id="element-93">
                    {plan.oldPrice}
                  </div>
                  <div className={`text-xl md:text-3xl font-extrabold mt-0.5 ${darkSelected ? 'text-white' : 'text-slate-900'}`} data-id="element-94">
                    {plan.price}
                  </div>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* Shared buy button — reflects the selected option */}
      <div className="max-w-3xl mx-auto mb-4 md:mb-5" data-id="element-95">
        <motion.a key={selected.id} href={selected.href} whileTap={{
        scale: 0.98
      }} initial={{
        opacity: 0.6
      }} animate={{
        opacity: 1
      }} className="w-full py-4 px-6 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 transition-colors flex items-center justify-center gap-2 text-base md:text-lg" data-id="element-96">
          👉 QUIERO {selected.name} — {selected.price}
          <ArrowRight className="w-5 h-5" data-id="element-97" />
        </motion.a>
      </div>

      {/* Trust Badges */}
      <div className="flex flex-wrap justify-center gap-3 md:gap-8 text-xs md:text-sm font-medium text-slate-500" data-id="element-98">
        <span className="flex items-center gap-1 md:gap-1.5" data-id="element-99">
          <Shield className="w-3 h-3 md:w-4 md:h-4" data-id="element-100" /> Pago seguro
        </span>
        <span className="flex items-center gap-1 md:gap-1.5" data-id="element-101">
          <Check className="w-3 h-3 md:w-4 md:h-4" data-id="element-102" /> Acceso en 5 min
        </span>
        <span className="flex items-center gap-1 md:gap-1.5" data-id="element-103">
          <Shield className="w-3 h-3 md:w-4 md:h-4" data-id="element-104" /> Garantía 7 días
        </span>
        <span className="flex items-center gap-1 md:gap-1.5" data-id="element-105">
          <Check className="w-3 h-3 md:w-4 md:h-4" data-id="element-106" /> Mercado Pago
        </span>
      </div>
    </div>;
}